/**
 * HOW A FEEDBACK SURFACE IS ANNOUNCED, decided once for every variant and read by every surface that shows one.
 *
 * THE ROLE AND THE POLITENESS ARE ONE DECISION, which is why they are one record. `role="alert"` is assertive by
 * definition and `role="status"` is polite by definition, so an element carrying one with the other's `aria-live`
 * tells assistive technology two different things about itself. Each surface deciding for itself is how that
 * contradiction arrives: the toast decided per variant and was right, the shared alert spelled `alert` with `polite`
 * for every variant including the two that must not interrupt anyone (operator's QA audit, round 2, 2026-09-15).
 *
 * WCAG's own line is the split: an error and a warning interrupt a reader mid-sentence because acting late costs
 * them; a success and an info wait for a pause, because they are the outcome a reader already asked for.
 */

import { ARIA_LIVE, ARIA_ROLES } from './../common/aria-tokens.ts';
import type { FeedbackVariant } from '@federa/types/ui-feedback-variant';

/** One surface's announcement: the role it is heard through and the politeness that role implies. */
interface FeedbackAnnouncement {
	readonly role: typeof ARIA_ROLES.alert | typeof ARIA_ROLES.status;
	readonly live: typeof ARIA_LIVE.assertive | typeof ARIA_LIVE.polite;
}

/** Interrupt the reader mid-sentence: acting late on this costs them. */
const INTERRUPT: FeedbackAnnouncement = { role: ARIA_ROLES.alert, live: ARIA_LIVE.assertive };

/** Wait for a pause: this is the outcome the reader already asked for. */
const WAIT_FOR_PAUSE: FeedbackAnnouncement = { role: ARIA_ROLES.status, live: ARIA_LIVE.polite };

/** Every variant's announcement. Total over the union, so a variant added there cannot reach a surface unannounced. */
export const FEEDBACK_ANNOUNCEMENT: Readonly<Record<FeedbackVariant, FeedbackAnnouncement>> = {
	error: INTERRUPT,
	warning: INTERRUPT,
	success: WAIT_FOR_PAUSE,
	info: WAIT_FOR_PAUSE,
};

/** @runtime-derived-ssot-helper: one variant's announcement, read off {@link FEEDBACK_ANNOUNCEMENT}. */
export function feedbackAnnouncementOf(variant: FeedbackVariant): FeedbackAnnouncement {
	return FEEDBACK_ANNOUNCEMENT[variant];
}
